import * as signalR from '@microsoft/signalr';

/** Backoff steps (ms) before the delay caps. Matches SignalR's default start, then holds at the cap. */
export const HUB_RETRY_DELAYS_MS: readonly number[] = [0, 2000, 10000, 30000];
export const HUB_RETRY_CAP_MS = 30000;

/**
 * #36932 #36938 #36956: SignalR's default policy gives up after ~42 s, but the hubs run in-process on
 * single-instance App Services, so a restart, deploy or sleeping tab kills the connection id for longer than
 * that. This policy never returns null: 0, 2, 10, 30, 30, ... seconds.
 */
export const neverGiveUpRetryPolicy: signalR.IRetryPolicy = {
  nextRetryDelayInMilliseconds: (context: signalR.RetryContext): number =>
    HUB_RETRY_DELAYS_MS[context.previousRetryCount] ?? HUB_RETRY_CAP_MS,
};

/**
 * #36956: with no logger configured, SignalR's transport uses a ConsoleLogger at Information, which writes
 * "Connection disconnected with error '...'" through console.error on every error close. Session replay and the
 * global error handling read console.error as an app error. Pass this to configureLogging so SignalR's own
 * warnings and errors go to console.warn, info stays info, and debug/trace stay silent.
 */
export function hubLogger(label: string): signalR.ILogger {
  return {
    log(logLevel: signalR.LogLevel, message: string): void {
      if (logLevel === signalR.LogLevel.None) {
        return;
      }
      if (logLevel >= signalR.LogLevel.Warning) {
        console.warn(`${label}: ${message}`);
      } else if (logLevel === signalR.LogLevel.Information) {
        console.info(`${label}: ${message}`);
      }
    },
  };
}

export interface HubKeepAlive {
  /** Arm restart-on-close plus visibility/online restarts. Call from the service's start(). */
  enable(): void;
  /** Disarm and remove listeners. Call from the service's stop() so an intentional stop stays stopped. */
  disable(): void;
  /** Schedule a timed restart on the backoff. Call when the first start() fails: no close event fires then. */
  retryLater(): void;
}

/**
 * Keeps a hub connection coming back after automatic reconnect has given up (or never existed): onclose
 * schedules a restart on the capped backoff, and a tab returning to visible or the browser coming back online
 * restarts at once. Reconnect/close are logged at warn/info, never error, so they do not reach the global
 * error handler.
 */
export function keepHubAlive(
  connection: signalR.HubConnection,
  label: string
): HubKeepAlive {
  let enabled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;

  const clearTimer = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const restart = async (): Promise<void> => {
    clearTimer();
    if (
      !enabled ||
      connection.state !== signalR.HubConnectionState.Disconnected
    ) {
      return;
    }
    try {
      await connection.start();
      attempt = 0;
      console.info(`${label}: hub connection restarted`);
    } catch (error) {
      console.warn(`${label}: hub restart failed, will retry`, error);
      schedule();
    }
  };

  const schedule = (): void => {
    if (!enabled || timer !== null) {
      return;
    }
    const delay = HUB_RETRY_DELAYS_MS[attempt] ?? HUB_RETRY_CAP_MS;
    attempt++;
    timer = setTimeout(() => {
      timer = null;
      void restart();
    }, delay);
  };

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') {
      void restart();
    }
  };
  const onOnline = (): void => {
    void restart();
  };

  connection.onclose((error) => {
    console.warn(`${label}: hub connection closed`, error);
    schedule();
  });
  connection.onreconnecting((error) => {
    console.warn(`${label}: hub reconnecting`, error);
  });
  connection.onreconnected((connectionId) => {
    attempt = 0;
    console.info(`${label}: hub reconnected`, connectionId);
  });

  return {
    enable(): void {
      if (enabled) {
        return;
      }
      enabled = true;
      attempt = 0;
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('online', onOnline);
    },
    retryLater(): void {
      schedule();
    },
    disable(): void {
      enabled = false;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    },
  };
}
